/** @jsxRuntime classic */
/** @jsx jsx */

import { css, jsx } from '@emotion/react';
import { Colors, Typography } from '@zapier/design-system';
import { labelStyles } from '@zapier/style-encapsulation';

import { RichTextHeading } from '../../RichTextHeading';
import { RichTextHTMLString } from '../types';
import { RichTextHtml } from '../../RichTextHtml/RichTextHtml';
import {
  MAX_WIDTH,
  MEDIA_MAX_WIDTH,
  MEDIA_MIN_WIDTH,
} from '../../../constants';
import { ScrollFade } from './ScrollFade';
import { useAnimationsSectionContext } from './Context';

type Props = {
  description?: RichTextHTMLString;
  heading: RichTextHTMLString;
  index: number;
};

const Styles = labelStyles('Text', {
  root: css({
    display: 'flex',
    alignItems: 'center',
    width: '100%',
    [MEDIA_MAX_WIDTH.large]: {
      margin: '0 0 50px 0',
    },
    [MEDIA_MAX_WIDTH.small]: {
      margin: '40px 0 40px 0',
    },
    [`${MEDIA_MIN_WIDTH.small} and ${MAX_WIDTH.large}`]: {
      width: '50%',
    },
  }),
  heading: css({
    ...Typography.pageHeader3,
    color: Colors.earth,
    [MEDIA_MAX_WIDTH.large]: {
      ...Typography.pageHeader7,
    },
  }),

  description: css({
    ...Typography.subHeader3,
    paddingTop: '22px',
    [MEDIA_MAX_WIDTH.large]: {
      ...Typography.paragraph3,
      paddingTop: '5px',
    },
  }),
});

export function Text({ heading, index, description }: Props) {
  const {
    textRootMargin,
    updateSectionIndex,
    isAnimationScrollable,
  } = useAnimationsSectionContext();

  return (
    <div css={Styles.root}>
      <div>
        <ScrollFade
          onVisible={() => {
            if (!isAnimationScrollable) updateSectionIndex(index);
          }}
          rootMargin={textRootMargin}
        >
          <RichTextHeading css={Styles.heading} html={heading} tag="h2" />
        </ScrollFade>
        {description && (
          <div css={Styles.description}>
            <ScrollFade rootMargin={textRootMargin}>
              <RichTextHtml html={description} />
            </ScrollFade>
          </div>
        )}
      </div>
    </div>
  );
}
